import { motion } from "framer-motion";
import { MapPin, Clock, ShieldCheck, BadgeCheck, Eye } from "lucide-react";

const PLUMBER_IMG =
  "https://customer-assets.emergentagent.com/job_drain-experts-ed/artifacts/0z1jl863_AI%20replacement.jpeg";

const items = [
  {
    icon: MapPin,
    title: "Local Edinburgh Business",
    desc: "Based in Edinburgh — we know the city, the tenements and the pipework.",
  },
  {
    icon: Clock,
    title: "24/7 Emergency Call-Outs",
    desc: "Day or night, weekends or holidays — we're on call when you need us most.",
  },
  {
    icon: ShieldCheck,
    title: "Fully Qualified & Insured",
    desc: "Time-served, fully insured tradesperson. Compliant, safe and accountable work.",
  },
  {
    icon: BadgeCheck,
    title: "No Hidden Charges",
    desc: "Honest, upfront pricing. The quote you get is the price you pay — no surprises.",
  },
  {
    icon: Eye,
    title: "Transparent & Honest",
    desc: "We explain exactly what we find, what needs doing and why — no unnecessary work, no confusing jargon.",
    wide: true,
  },
];

const About = () => {
  return (
    <section
      id="about"
      className="relative py-20 md:py-32 bg-[#151D33] border-y border-[#2C374D]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: image + heading */}
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D47A4A] mb-4">
              / 02 — Why Choose Us
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] tracking-tight">
              REAL TRADE.
              <br />
              <span className="text-[#D47A4A]">REAL PEOPLE.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#A0ABC0] max-w-md">
              A trusted local team delivering consistent, high quality work on
              every job.
            </p>

            <div className="relative mt-10 overflow-hidden rounded-sm border border-[#2C374D]">
              <img
                src={PLUMBER_IMG}
                alt="Professional plumber at work"
                className="w-full h-72 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="font-display text-white text-xl tracking-wide">
                  ON THE TOOLS, ON YOUR DOORSTEP.
                </span>
              </div>
            </div>
          </div>

          {/* Right: feature list */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`bg-[#0B132B] border border-[#2C374D] hover:border-[#D47A4A]/60 p-6 md:p-7 rounded-sm transition-colors ${
                    it.wide ? "sm:col-span-2" : ""
                  }`}
                  data-testid={`trust-item-${i + 1}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D47A4A]/10 border border-[#D47A4A]/30 rounded-sm mb-5">
                    <Icon className="w-6 h-6 text-[#D47A4A]" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-white tracking-wide mb-2">
                    {it.title.toUpperCase()}
                  </h3>
                  <p className="text-sm text-[#A0ABC0] leading-relaxed">
                    {it.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
